import { AppNamesEnum } from "@/enums/appSettings";
import { handleApiError, handleApiResponse } from "@/methods/api";
import type {
  OlympiadAffiliationByCode,
  OlympiadBatchDisplay,
  OlympiadDisplay,
  OlympiadGradeDetail,
  OlympiadStudentInputs,
} from "@/types/olympiad";
import {
  AccountValidationOtpRequest,
  AffiliationCustomAssets,
  ApiResponse,
  AppVersionInfo,
  CourseForSignup,
  LoginWithOtp,
  LoginWithPassword,
  RegisterB2CUser,
  ServerClock,
  UserInfo,
} from "@/types/global";
import type { RegisterB2CGoogleUser, RegisterB2CWithOtpInputs } from "@/types/userManagement";
import axios, { AxiosInstance } from "axios";
// `axios.defaults.headers.common["Access-Control-Allow-Origin"] = "*"` used to be set here. It was
// inert as a request header — Access-Control-Allow-Origin is something a *server* answers with,
// and the browser ignores it on the way out — but it was not harmless. It is not a CORS-safelisted
// request header, so its presence promoted every call in the app to a preflighted request, and it
// landed on `axios.defaults`, meaning every instance inherited it, not just this one.
//
// The cost fell hardest exactly where it hurts: getServerClock() and getLatestAppVersionInfo() are
// the two calls that decide whether the user sees the app or /under-maintenance, and they are the
// only boot calls with no Authorization header, so without this they are simple GETs needing no
// preflight at all. With it, each one cost an extra OPTIONS round trip against the 10s timeout,
// and any network that filters OPTIONS (school/corporate proxies do) failed them outright while
// serving the page itself normally — which is precisely the reported symptom.

export default class UnverifiedUserApi {
  private axiosInstance: AxiosInstance;

  constructor(apiBaseUrl: string) {
    this.axiosInstance = axios.create({
      baseURL: `${apiBaseUrl}/`,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(key, String(v)));
          } else if (value !== null && value !== undefined) {
            searchParams.append(key, String(value));
          }
        }
        return searchParams.toString();
      },
    });
  }

  async getServerClock(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ServerClock>(`unverified-user/server-clock`, {
        timeout: 10000,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async loginWithPassword(loginCredentials: LoginWithPassword): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<UserInfo>(
        `unverified-user/login-with-password`,
        loginCredentials,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async sendLoginOtp(loginId: string, appName: AppNamesEnum): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<string>(`unverified-user/send-login-otp`, {
        loginId: loginId,
        appName: appName,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async loginWithOtp(loginCredentials: LoginWithOtp): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<UserInfo>(
        `unverified-user/login-with-otp`,
        loginCredentials,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async checkDuplicateLoginSession(userId: number, loginSessionId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<string>(
        `unverified-user/check-duplicate-login-session`,
        { params: { userId: userId, loginSessionId: loginSessionId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async expireLoginSession(loginSessionId: number, isSystemExpired: boolean): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `unverified-user/expire-login-session`,
        {
          LoginSessionId: loginSessionId,
          IsSystemExpired: isSystemExpired,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async registerB2CUser(registerCredentials: RegisterB2CUser): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `unverified-user/register-b2c-user`,
        registerCredentials,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAffiliationCustomAssets(domainName: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AffiliationCustomAssets>(
        `unverified-user/get-affiliation-custom-assets`,
        { params: { domainName: domainName } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getLatestAppVersionInfo(appName: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AppVersionInfo>(
        `unverified-user/latest-app-version-info`,
        { params: { appName: appName } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async refreshAccessToken(refreshToken: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<string>(
        `unverified-user/refresh-access-token`,
        { params: { refreshToken: refreshToken } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getCoursesForSignup(): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<CourseForSignup>>(
        `unverified-user/courses-for-signup`,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async sendRegisterOtp(
    mobileNumber: string,
    userName: string,
    appName: AppNamesEnum,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<string>(`unverified-user/send-register-otp`, {
        mobileNumber: mobileNumber,
        userName: userName,
        appName: appName,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async validateOtp(otpId: string, otp: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<boolean>(`unverified-user/validate-otp`, {
        params: { otpId: otpId, otp: otp },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // User Management v3 (pvni-user-mgmt-v3) — B2C OTP registration (SP3, api-binding §2).
  // POST /v7/unverified-user/verify-otp-and-register-b2c → UserInfo (store tokens, go to dashboard).
  // 401 "Please check the OTP again." · 422 password rule · 500 "Creating new user in DB failed!".
  async verifyOtpAndRegisterB2C(inputs: RegisterB2CWithOtpInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<UserInfo>(
        `unverified-user/verify-otp-and-register-b2c`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // User Management v3 (pvni-user-mgmt-v3) — B2C Google SSO registration (SP4, api-binding §3).
  // POST /v7/unverified-user/register-b2c-google-user → UserInfo (RoleId=17) + linked credential.
  // 422 no usable email / bad deviceTypeId · 409 email/Google already linked · 429 rate limit · 500.
  async registerB2CGoogleUser(inputs: RegisterB2CGoogleUser): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<UserInfo>(
        `unverified-user/register-b2c-google-user`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async sendOtpForAccountValidation(
    passwordResetOtpRequest: AccountValidationOtpRequest,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<string>(
        `unverified-user/send-otp-for-account-validation`,
        passwordResetOtpRequest,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async resetForgotPassword(
    loginId: string,
    newPassword: string,
    appName: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `unverified-user/reset-forgot-password`,
        { LoginId: loginId, NewPassword: newPassword, AppName: appName },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ─── Olympiad public landing (plan-§5.14) ───────────────────────────────────
  // These carry no bearer token, so the 403 refresh-and-replay behaviour in apiVerified does
  // not apply to them. Shapes verified against docs/api/unverified-user.json.

  /** plan-§5.14 — GET unverified-user/olympiad-display. Landing config; 404 if the code is unknown. */
  async getOlympiadDisplay(olympiadCode: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<OlympiadDisplay>(
        `unverified-user/olympiad-display`,
        { params: { olympiadCode: olympiadCode } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /** plan-§5.14 — GET unverified-user/all-grades-by-olympiad. */
  async getAllGradesByOlympiad(olympiadId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<OlympiadGradeDetail>>(
        `unverified-user/all-grades-by-olympiad`,
        { params: { olympiadId: olympiadId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * plan-§5.14 — GET unverified-user/olympiad-affiliation-by-code.
   *
   * Takes BOTH olympiadId and affiliationCode: the plan documented only the code, but the
   * OpenAPI spec marks both as required. A school code is only unique within an olympiad.
   */
  async getOlympiadAffiliationByCode(
    olympiadId: number,
    affiliationCode: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<OlympiadAffiliationByCode>(
        `unverified-user/olympiad-affiliation-by-code`,
        { params: { olympiadId: olympiadId, affiliationCode: affiliationCode } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /**
   * plan-REV-1 — GET unverified-user/all-olympiad-affiliation-batches.
   *
   * The sixth public route, absent from the handoff's §5.14 list. It is the link that makes
   * public student registration possible: register-olympiad-student requires a BatchId, and
   * this is the only public endpoint that yields one. Each row carries GradeName, so the
   * visitor picks a grade and never sees a batch id (BR-UI-049).
   */
  async getAllOlympiadAffiliationBatches(affiliationId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<OlympiadBatchDisplay>>(
        `unverified-user/all-olympiad-affiliation-batches`,
        { params: { affiliationId: affiliationId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  /** plan-§5.14 — POST unverified-user/register-olympiad-student. Returns the login id. */
  async registerPublicOlympiadStudent(inputs: OlympiadStudentInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<string>(
        `unverified-user/register-olympiad-student`,
        inputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

}
